import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SitePhoto, SitePhotoPost } from '@/lib/apiClient';
import { resolveDownloadUrl, resolveThumbnails } from '@/lib/graphUpload';
import { holdNewest } from '@/lib/heldFiles';
import { viewerImage } from '@/lib/viewerImage';
import { isTenantSharePointUrl } from '@/lib/sharepointUrl';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { fmtDate } from '@/lib/dates';
import { describeDistance, distanceMeters, mapUrl } from '@/lib/photoMeta';

/**
 * Full-screen photo viewer for the site photo thread.
 *
 *  - Swipe or ← / → to move between photos; Esc or × closes.
 *  - Pinch, mouse wheel, or double-tap to zoom (up to 5×); drag to reposition
 *    while zoomed (#328: "zoom and position/drag it for best views").
 *  - Download fetches the ORIGINAL file from SharePoint (#328), falling back to
 *    opening it in SharePoint when the direct link can't be had.
 *
 * Shows the large Graph thumbnail first, which keeps it quick on a phone, then
 * swaps in the stored file itself once it has loaded. The large thumbnail is
 * only 800 px on its longest edge (Graph's documented size), so zooming it
 * 2.5× on a desktop drew each pixel four times over and every photo looked
 * fuzzy (#665 follow-up). The stored file is what the composer uploaded:
 * SITE_PHOTO_MAX_DIM now, 1920 px before. Only the photo on screen gets the
 * file; its two neighbors get the thumbnail — asking SharePoint for every
 * loaded photo at once is what gets a busy thread throttled.
 */

export interface ViewerItem { post: SitePhotoPost; photo: SitePhoto }

const MAX_ZOOM = 5;

/** Large thumbnail for one photo, cached per link like the medium ones. */
const largeKey = (webUrl: string) => ['site-photo-large', webUrl];
const largeQuery = (webUrl: string, getToken: () => Promise<string>) => ({
  queryKey: largeKey(webUrl),
  queryFn: async () => (await resolveThumbnails([webUrl], getToken, 'large'))[webUrl] ?? null,
  staleTime: 4 * 60 * 1000, // thumbnail URLs are short-lived but reusable for a few minutes
});

/** How long a photo stays on screen before its stored file is fetched, so a
 *  fast swipe through a post doesn't download every file in it. */
const FULL_FILE_DWELL_MS = 400;

/** How many stored files the viewer keeps in memory at once. At 3000 px each
 *  is 1–3 MB, and keeping every photo stepped through until the viewer closed
 *  could run a phone out of memory on a busy job (review of the #665
 *  follow-up). The oldest is let go first; coming back to it fetches it again. */
const MAX_HELD_FILES = 8;

/**
 * The stored file as an object URL the viewer owns; null when it was fetched
 * but can never be shown (a format the browser can't draw, like a HEIC that
 * passed through the composer uncompressed); undefined when the attempt
 * failed for now (no download link, which can't be told apart from a dropped
 * connection; a refused request; a swipe away). The viewer keeps the
 * thumbnail either way, but remembers only null, so a photo that failed on a
 * weak site connection is tried again when it is shown again.
 *
 * Fetched as bytes rather than drawn from the download link, for three
 * reasons (review of the #665 follow-up): the link is short-lived, so a
 * cached one could later draw a broken image; storage can label a good JPEG
 * application/octet-stream (see weekly/reportPhotos.ts, which fetches the
 * same link), which a browser may refuse as a cross-origin <img>; and a
 * preload followed by an <img> of the same link could download it twice.
 * Resolves only once the browser has decoded it, so the swap never flashes.
 */
async function loadStoredFile(
  webUrl: string, getToken: () => Promise<string>, signal: AbortSignal,
): Promise<string | null | undefined> {
  // A missing link is as likely a dropped connection as a missing file, and
  // resolveDownloadUrl can't say which, so it counts as "not now".
  const link = await resolveDownloadUrl(webUrl, getToken, signal);
  if (!link || signal.aborted) return undefined;
  try {
    // Pre-authenticated: fetched WITHOUT an Authorization header.
    const res = await fetch(link, { signal });
    if (!res.ok) return undefined;
    const blob = await res.blob();
    const typed = blob.type.startsWith('image/') ? blob : new Blob([blob], { type: 'image/jpeg' });
    const url = URL.createObjectURL(typed);
    if (await canDraw(url)) return url;
    URL.revokeObjectURL(url);
    return null; // fetched, but the browser can't draw it: never will
  } catch {
    return undefined; // aborted, or the network failed: try again next time
  }
}

/**
 * Whether the browser can draw this image, decided by actually DECODING it,
 * not just loading it: the swap then shows an image that is already decoded,
 * rather than decoding a 3000 px JPEG on the main thread mid-pinch. decode()
 * rejects for a format the browser can't draw.
 */
async function canDraw(url: string): Promise<boolean> {
  const img = new Image();
  img.src = url;
  try {
    await img.decode();
    return img.naturalWidth > 0;
  } catch {
    return false;
  }
}

const css = `
  .spv-backdrop {
    position: fixed; inset: 0; z-index: 1100; background: rgba(0, 0, 0, 0.94);
    display: flex; flex-direction: column; color: #fff;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
  }
  .spv-top {
    display: flex; align-items: center; gap: var(--ew-s-2);
    padding: var(--ew-s-2) var(--ew-s-3); flex: none;
  }
  .spv-count { color: rgba(255, 255, 255, 0.7); font-variant-numeric: tabular-nums; }
  .spv-btn {
    background: rgba(255, 255, 255, 0.12); color: #fff; border: 0; border-radius: var(--ew-r-md);
    padding: 8px 12px; cursor: pointer; font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    text-decoration: none; display: inline-flex; align-items: center; gap: 6px; min-height: 36px;
  }
  .spv-btn:hover { background: rgba(255, 255, 255, 0.22); }
  .spv-btn:disabled { opacity: 0.6; cursor: default; }
  .spv-close { margin-left: auto; font-size: 22px; padding: 4px 12px; }
  .spv-stage {
    flex: 1; min-height: 0; position: relative; overflow: hidden;
    touch-action: none; user-select: none; -webkit-user-select: none;
    display: flex; align-items: center; justify-content: center;
  }
  .spv-img {
    display: block; object-fit: contain;
    transform-origin: center center; will-change: transform; pointer-events: none;
  }
  /* While the stored file is coming or shown: the stage's size, not the
     source's, so the 800 px thumbnail and the 3000 px file occupy the same box
     and swapping one for the other under a zoom doesn't move the photo. */
  .spv-img.spv-fill { position: absolute; inset: 0; width: 100%; height: 100%; }
  /* Settled on the thumbnail (a HEIC stored uncompressed, a failed fetch):
     never larger than its own pixels, so it isn't stretched soft. A photo
     already filling when a fetch fails mid-zoom keeps filling until the
     reader zooms out; lib/viewerImage.ts decides. */
  .spv-img.spv-natural { max-width: 100%; max-height: 100%; }
  .spv-img.spv-anim { transition: transform 180ms var(--ew-ease); }
  .spv-placeholder { color: rgba(255, 255, 255, 0.7); padding: var(--ew-s-5); text-align: center; }
  .spv-nav {
    position: absolute; top: 50%; transform: translateY(-50%); z-index: 1;
    width: 44px; height: 44px; border-radius: 50%; border: 0; cursor: pointer;
    background: rgba(0, 0, 0, 0.45); color: #fff; font-size: 22px; line-height: 1;
  }
  .spv-nav:hover { background: rgba(0, 0, 0, 0.7); }
  .spv-prev { left: 12px; }
  .spv-next { right: 12px; }
  @media (pointer: coarse) { .spv-nav { display: none; } }
  .spv-info {
    flex: none; padding: var(--ew-s-3) var(--ew-s-4) calc(var(--ew-s-3) + env(safe-area-inset-bottom));
    display: grid; gap: 4px; background: rgba(0, 0, 0, 0.5);
  }
  .spv-caption { font: var(--ew-fw-medium) var(--ew-fs-body-ui)/1.4 var(--ew-font); white-space: pre-wrap; }
  .spv-meta { color: rgba(255, 255, 255, 0.75); display: flex; flex-wrap: wrap; gap: 4px 14px; }
  .spv-meta a { color: #9fd3b4; }
  .spv-hint { color: rgba(255, 255, 255, 0.5); font-size: var(--ew-fs-meta); }
`;

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function SitePhotoViewer({
  items,
  startIndex,
  onClose,
  getToken,
  getTokenSilent,
  site,
}: {
  items: ViewerItem[];
  startIndex: number;
  onClose: () => void;
  /** May show a sign-in popup — only call from a tap (Download). */
  getToken: () => Promise<string>;
  /** Never shows a popup; for fetching previews. */
  getTokenSilent: () => Promise<string>;
  /** The job's pin, for "On site" / "N mi from site". */
  site: { latitude: number; longitude: number } | null;
}) {
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState({ s: 1, x: 0, y: 0 });
  const [animate, setAnimate] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // Tab stays inside the viewer, and focus goes back where it was on close
  // (review of #560). Escape is handled by the window listener below.
  useFocusTrap(rootRef);
  const qc = useQueryClient();
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{
    startDist?: number; startScale?: number;
    lastX?: number; lastY?: number;
    swipeStartX?: number; swipeStartY?: number;
    lastTap?: number;
  }>({});

  const item = items[index];
  const count = items.length;

  const large = useQuery({
    ...largeQuery(item?.photo.webUrl ?? '', getTokenSilent),
    enabled: !!item,
  });
  // The sharp version: each photo's stored file, by link, once fetched (null =
  // can't be drawn). Held here, not in the query cache, because the object
  // URLs belong to this viewer and are revoked when it closes.
  const [files, setFiles] = useState<Record<string, string | null>>({});
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(() => () => {
    for (const u of Object.values(filesRef.current)) if (u) URL.revokeObjectURL(u);
  }, []);
  // The photo whose file failed for now ON THIS VISIT, so the stage stops
  // saying "Loading…" for it. Reset when another photo comes on screen, so
  // coming back retries it; set only by a fetch for the photo on screen, so a
  // revisit never renders first as failed and then as pending.
  const [failedThisVisit, setFailedThisVisit] = useState<string | null>(null);
  const webUrl = item?.photo.webUrl;
  useEffect(() => { setFailedThisVisit(null); }, [webUrl]);
  // After the thumbnail has had its turn, so it still shows first, and after
  // the photo has stayed on screen a moment. Swiping away cancels it. Tried
  // even when there is no thumbnail: SharePoint can be slow to make one for a
  // new file, and the file itself may already draw.
  useEffect(() => {
    if (!webUrl || large.isLoading || webUrl in filesRef.current) return;
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      void loadStoredFile(webUrl, getTokenSilent, ctl.signal).then((url) => {
        if (ctl.signal.aborted) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url === undefined) {
          // Failed for now: show the thumbnail, and try again next time.
          setFailedThisVisit(webUrl);
          return;
        }
        setFiles((f) => holdNewest(f, webUrl, url, MAX_HELD_FILES));
      });
    }, FULL_FILE_DWELL_MS);
    return () => { clearTimeout(timer); ctl.abort(); };
  }, [webUrl, large.isLoading]); // getTokenSilent left out: it is a new function every render
  // Warm the neighbors so a swipe shows the next photo straight away.
  useEffect(() => {
    for (const n of [items[index - 1], items[index + 1]]) {
      if (n) void qc.prefetchQuery(largeQuery(n.photo.webUrl, getTokenSilent));
    }
  }, [index, items, qc]); // getTokenSilent left out: it is a new function every render

  // Keyboard users land on Close. Declared after useFocusTrap so it runs
  // after the trap's own initial focus.
  useEffect(() => { closeRef.current?.focus(); }, []);

  const go = useCallback((delta: number) => {
    setIndex((i) => Math.min(count - 1, Math.max(0, i + delta)));
    setZoom({ s: 1, x: 0, y: 0 });
  }, [count]);

  // Keyboard: ← → Esc. Also lock page scroll behind the viewer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [go, onClose]);

  const clamp = (s: number) => Math.min(MAX_ZOOM, Math.max(1, s));
  const setScale = (s: number, anim = false) => {
    setAnimate(anim);
    setZoom((z) => {
      const ns = clamp(s);
      // Zooming back out recenters; otherwise keep the pan, scaled.
      return ns === 1 ? { s: 1, x: 0, y: 0 } : { s: ns, x: z.x * (ns / z.s), y: z.y * (ns / z.s) };
    });
  };

  // Wheel zoom (desktop). Non-passive so the page doesn't scroll.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setAnimate(false);
      setZoom((z) => {
        const ns = clamp(z.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
        return ns === 1 ? { s: 1, x: 0, y: 0 } : { s: ns, x: z.x * (ns / z.s), y: z.y * (ns / z.s) };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const dist = () => {
    const [a, b] = [...pointers.current.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    // The prev/next buttons sit on the stage; let their clicks be clicks.
    if ((e.target as HTMLElement).closest('button')) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (pointers.current.size === 2) {
      g.startDist = dist();
      g.startScale = zoom.s;
    } else if (pointers.current.size === 1) {
      g.lastX = e.clientX; g.lastY = e.clientY;
      g.swipeStartX = e.clientX; g.swipeStartY = e.clientY;
      // Double-tap / double-click toggles zoom.
      const now = Date.now();
      if (g.lastTap && now - g.lastTap < 300) {
        setScale(zoom.s > 1 ? 1 : 2.5, true);
        g.lastTap = 0;
      } else {
        g.lastTap = now;
      }
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (pointers.current.size === 2 && g.startDist) {
      setAnimate(false);
      const s = clamp((g.startScale ?? 1) * (dist() / g.startDist));
      setZoom((z) => (s === 1 ? { s: 1, x: 0, y: 0 } : { ...z, s }));
    } else if (pointers.current.size === 1 && zoom.s > 1 && g.lastX !== undefined && g.lastY !== undefined) {
      setAnimate(false);
      const dx = e.clientX - g.lastX;
      const dy = e.clientY - g.lastY;
      g.lastX = e.clientX; g.lastY = e.clientY;
      setZoom((z) => ({ ...z, x: z.x + dx, y: z.y + dy }));
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const g = gesture.current;
    const wasSingle = pointers.current.size === 1;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) g.startDist = undefined;
    // A horizontal swipe at 1× moves between photos.
    if (wasSingle && zoom.s === 1 && g.swipeStartX !== undefined && g.swipeStartY !== undefined) {
      const dx = e.clientX - g.swipeStartX;
      const dy = e.clientY - g.swipeStartY;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
    }
  };

  const download = () => {
    if (!item) return;
    const { webUrl, fileName } = item.photo;
    // Open the tab NOW, inside the tap. A tab opened only after awaiting a
    // token and a Graph lookup is the kind mobile Safari may silently block
    // as an unrequested popup (review of #560); an already-open tab can be
    // pointed at the file whenever the link arrives.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setDownloading(true);
    void resolveDownloadUrl(webUrl, getToken)
      .then((url) => {
        const target = url ?? (isTenantSharePointUrl(webUrl) ? webUrl : null);
        if (tab) {
          if (target) tab.location.href = target;
          else tab.close();
          return;
        }
        // Pop-ups blocked outright: last resort, the old anchor route.
        if (!target) return;
        const a = document.createElement('a');
        a.href = target;
        a.download = fileName;
        a.target = '_blank';
        a.rel = 'noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .finally(() => setDownloading(false));
  };

  // Same <img> either way (keyed by photo); viewerImage decides the source and
  // whether it fills the stage (see lib/viewerImage.ts). It needs whether
  // THIS photo was filling at the last render: a photo that was filling keeps
  // filling while zoomed (a fetch failed mid-zoom), and one that wasn't
  // doesn't start when the reader zooms into it.
  const lastFill = useRef<{ id: number; fill: boolean } | null>(null);
  const shown = item ? viewerImage({
    file: files[item.photo.webUrl],
    filePending: !(item.photo.webUrl in files) && failedThisVisit !== item.photo.webUrl,
    thumb: large.data,
    thumbLoading: large.isLoading,
    zoomed: zoom.s > 1,
    wasFilling: lastFill.current?.id === item.photo.id && lastFill.current.fill,
  }) : null;
  const shownFill = shown?.kind === 'image' && shown.fill;
  useEffect(() => {
    if (item) lastFill.current = { id: item.photo.id, fill: shownFill };
  }, [item, shownFill]);

  if (!item || !shown) return null;
  const { post, photo } = item;
  const safeLink = isTenantSharePointUrl(photo.webUrl) ? photo.webUrl : null;
  const located = photo.latitude != null && photo.longitude != null;
  const distance = located && site
    ? describeDistance(distanceMeters(site, { latitude: photo.latitude!, longitude: photo.longitude! }))
    : null;

  return (
    <div ref={rootRef} className="spv-backdrop" role="dialog" aria-modal="true" aria-label="Photo viewer">
      <style>{css}</style>
      <div className="spv-top">
        <span className="spv-count">{index + 1} / {count}</span>
        <button type="button" className="spv-btn" onClick={download} disabled={downloading}>
          {downloading ? 'Preparing…' : '⤓ Download'}
        </button>
        {safeLink ? <a className="spv-btn" href={safeLink} target="_blank" rel="noreferrer">Open in SharePoint</a> : null}
        {zoom.s > 1 ? (
          <button type="button" className="spv-btn" onClick={() => setScale(1, true)}>Reset zoom</button>
        ) : null}
        <button ref={closeRef} type="button" className="spv-btn spv-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div
        ref={stageRef}
        className="spv-stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {shown.kind === 'loading' ? (
          <div className="spv-placeholder">Loading…</div>
        ) : shown.kind === 'image' ? (
          <img
            key={photo.id}
            className={`spv-img ${shown.fill ? 'spv-fill' : 'spv-natural'}${animate ? ' spv-anim' : ''}`}
            src={shown.src}
            alt={post.caption ?? photo.fileName}
            draggable={false}
            style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})` }}
          />
        ) : (
          <div className="spv-placeholder">
            Preview not available.<br />Use Download or Open in SharePoint to see this photo.
          </div>
        )}
        {index > 0 ? (
          <button type="button" className="spv-nav spv-prev" onClick={() => go(-1)} aria-label="Previous photo">‹</button>
        ) : null}
        {index < count - 1 ? (
          <button type="button" className="spv-nav spv-next" onClick={() => go(1)} aria-label="Next photo">›</button>
        ) : null}
      </div>

      <div className="spv-info">
        {post.caption ? <div className="spv-caption">{post.caption}</div> : null}
        <div className="spv-meta">
          <span>{photo.takenAt ? `Taken ${fmtTime(photo.takenAt)}` : `Site day ${fmtDate(post.photoDate)}`}</span>
          <span>Posted by {post.author?.displayName ?? 'Unknown'} · {fmtTime(post.createdAt)}</span>
          {located ? (
            <span>
              {photo.locationSource === 'device' ? 'Uploaded from ' : 'Taken at '}
              <a href={mapUrl(photo.latitude!, photo.longitude!)} target="_blank" rel="noreferrer">
                {photo.latitude!.toFixed(5)}, {photo.longitude!.toFixed(5)}
              </a>
              {distance ? ` · ${distance}` : ''}
            </span>
          ) : (
            <span>No location</span>
          )}
        </div>
        <div className="spv-hint">Pinch, scroll or double-tap to zoom · drag to move · swipe for the next photo</div>
      </div>
    </div>
  );
}
